import Copy from '../assets/copy.json';
import { colors, palette } from './theme.colors';

export enum FontSize {
  Fine = 'fine',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Inherit = 'inherit',
  H1 = 'H1',
  H2 = 'H2',
  H3 = 'H3',
  H4 = 'H4',
  H5 = 'H5',
  H6 = 'H6',
}

export enum FontType {
  Regular = 'regular',
  SemiBold = 'semibold',
  Strong = 'strong',
  Light = 'light',
}

const buttonType = {
  margin: 'auto',
  maxWidth: '200px',
  padding: '15px 26px 14px',
  radius: '4px',
};

const inputType = {
  borderBottom: `1px solid ${palette.grey200}`,
  fontSize: FontSize.H4,
  fontWeight: FontType.Light,
  letterSpacing: '8px',
  maxWidth: '500px',
  placeholderColor: palette.grey200,
  placeholderFontSize: '35px',
};

const logo = {
  boxShadow: 'none',
  image: '',
  margin: 'auto',
  paddingLeft: '24px',
  tabletWidth: '60%',
  width: '170px',
};

const uploadPage = {
  backgroundColor: '#f7f8fa',
  buttonCase: 'uppercase',
  containerMaxWidth: '100%',
  containerRadius: '0px',
  fileContainerSize: 'auto',
  formsDisplay: 'flex',
  headerBackgroundColor: colors.white,
  headerPadding: '80px 0px 0px 80px',
  margin: '0px',
  mobileHeaderPadding: '0px',
  sceneBackgroundColor: '#f7f8fa',
  tabTitleBottomPadding: '20px',
  titleBottomPadding: '35px',
  titleColor: colors.black,
  titleTopPadding: '0px',
  uploadIconDisplay: 'none',
};

const maintenanceInfo = {
  color: 'red',
  fontSize: '18px',
  margin: '20px',
};

const icon = {
  left: '15',
  position: 'absolute',
  top: '15',
};

const finishButton = {
  bottom: '30px',
  height: '60px',
  left: '50%',
  marginLeft: '-100px',
  mobileBorderRadius: '0px',
  mobileBottom: '0',
  mobileLeft: '0',
  mobileMargin: '0px',
  mobileWidth: '100vw',
  position: 'fixed',
  transformFrom: 'translateY(40px)',
  transformTo: 'translateY(0px)',
  width: '220px',
};

const uploadButton = {
  backgroundColor: '#e7eaec',
  buttonCase: 'capitalize',
  color: palette.grey700,
  height: '90px',
  hover: '#e7eaec',
  iconColor: palette.grey700,
  iconSize: '35',
  marginLeft: '80px',
  transform: '-5px',
};

const toaster = {
  bottom: '-20',
  radius: '8px',
  right: 'auto',
};

const customMediaQuery = (maxWidth: number) =>
  `@media (max-width: ${maxWidth}px)`;

const breakpoints = {
  desktop: 1200,
  mobile: 600,
  tablet: 700,
};

const copy = Copy;

const theme = {
  breakpoints,
  buttonType,
  colors,
  copy,
  finishButton,
  font(fontType: FontType, fontSize: FontSize) {
    const { font, weight } = this.fonts[fontType];

    return `
      color: ${this.fonts.fontColors[fontSize]};
      font-size: ${this.fonts.size[fontSize]};
      line-height: ${this.fonts.lineHeight[fontSize]};
      font-family: ${font};
      font-weight: ${weight};
      letter-spacing: ${this.fonts.letterSpacing[fontSize]};
    `;
  },
  fonts: {
    fontColors: {
      [FontSize.Inherit]: colors.text,
      [FontSize.Large]: colors.text,
      [FontSize.Medium]: colors.text,
      [FontSize.Small]: colors.text,
      [FontSize.Fine]: colors.text,
      [FontSize.H1]: colors.text,
      [FontSize.H2]: colors.textDarker,
      [FontSize.H3]: colors.textDarker,
      [FontSize.H4]: colors.textDark,
      [FontSize.H5]: colors.text,
      [FontSize.H6]: colors.text,
    },
    letterSpacing: {
      [FontSize.Inherit]: 'normal',
      [FontSize.Large]: '0.5px',
      [FontSize.Medium]: '.86px',
      [FontSize.Small]: 'normal',
      [FontSize.Fine]: 'normal',
      [FontSize.H1]: 'normal',
      [FontSize.H2]: 'normal',
      [FontSize.H3]: 'normal',
      [FontSize.H4]: 'normal',
      [FontSize.H5]: 'normal',
      [FontSize.H6]: 'normal',
    },
    lineHeight: {
      [FontSize.Inherit]: 'normal',
      [FontSize.Large]: `${70 / 60}`,
      [FontSize.Medium]: `${45 / 32}`,
      [FontSize.Small]: `${20 / 18}`,
      [FontSize.Fine]: 'normal',
      [FontSize.H1]: `${70 / 60}`,
      [FontSize.H2]: `${45 / 32}`,
      [FontSize.H3]: `${20 / 18}`,
      [FontSize.H4]: `${34 / 28}`,
      [FontSize.H5]: `${34 / 28}`,
      [FontSize.H6]: `${34 / 28}`,
    },
    [FontType.Light]: {
      font: 'Gotham-Light',
      weight: '200',
    },
    [FontType.Regular]: {
      font: 'Gotham-Book',
      weight: '400',
    },
    [FontType.SemiBold]: {
      font: 'Gotham-Bold',
      weight: '600',
    },
    [FontType.Strong]: {
      font: 'Gotham-Bold',
      weight: '700',
    },
    size: {
      [FontSize.Inherit]: '1em',
      [FontSize.Large]: '1.25em',
      [FontSize.Medium]: '1em',
      [FontSize.Small]: '.75em',
      [FontSize.Fine]: '.5em',
      [FontSize.H1]: '101px',
      [FontSize.H2]: '77px',
      [FontSize.H3]: '58px',
      [FontSize.H4]: '48px',
      [FontSize.H5]: '36px',
      [FontSize.H6]: '28px',
    },
  },
  icon,
  inputType,
  logo,
  maintenanceInfo,
  media: {
    custom: customMediaQuery,
    desktop: customMediaQuery(breakpoints.desktop),
    mobile: customMediaQuery(breakpoints.mobile),
    tablet: customMediaQuery(breakpoints.tablet),
  },
  palette,
  toaster,
  uploadButton,
  uploadPage,
};

export type ThemeType = typeof theme;

export default theme;
